/**
 * Program eligibility rules, shared by every screen that enforces them.
 *
 * Four hard stops. Three are keyed off the applicant's law degree date; the
 * fourth is keyed off how far into law school they are.
 *
 *   1. FIRST SEMESTER  — a 1L in their first semester is too early for every
 *                        program. Independent of the date and of the program.
 *   2. TOO SENIOR      — degree conferred before the cycle's floor. Blocks every
 *                        program.
 *   3. HP CEILING      — a current law student graduating after the cycle's
 *                        student ceiling cannot use the Honors Program and
 *                        belongs in SLIP-Pathways.
 *   4. LAW CLERK FLOOR — SLIP-Law Clerk exists for the summer between graduation
 *                        and a clerkship, so a degree conferred before the
 *                        cycle's law-clerk floor is too early for that track.
 *
 * ## Thresholds are derived, never typed
 *
 * The three date rules all fall on October 1, offset from the cycle year, so a
 * new cycle is a cycle-year change and nothing else:
 *
 *   cycle 2027 -> too senior before 2023-10-01
 *                 HP students not after 2027-10-01
 *                 law clerks not before 2026-10-01
 *
 * October 1 is itself ELIGIBLE in all three — it is the last good day for the
 * ceiling and the first good day for the floors. The acceptance criteria quote
 * the same three dates for the 2027 cycle; deriving them keeps the copy and the
 * logic from drifting apart next summer.
 *
 * ## Relationship to `cycle-eligibility.config.ts`
 *
 * That config drives EPA *progressive disclosure* — which parts of the timeline
 * to surface — and explicitly does not reject anyone. It is deliberately left
 * alone here: these rules are the blocking layer, and conflating the two would
 * change disclosure behaviour as a side effect of an eligibility change.
 */

/** The one date format we accept, matching the `<input type="date">` value. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True only for a complete `YYYY-MM-DD`. Local to this module so `core` never
 * imports from a page section; `timeline.logic.ts` has the equivalent check for
 * timeline entries.
 */
const isCompleteDate = (value: string | null | undefined): boolean => !!value && ISO_DATE.test(value);

/** The three graduation-date thresholds for a cycle. All are inclusive-eligible. */
export interface ProgramEligibilityThresholds {
	/** Degrees conferred BEFORE this date are too senior for any program. */
	tooSeniorBefore: string;
	/** The latest graduation date a current student may have and still use HP. */
	hpStudentLatest: string;
	/** The earliest graduation date SLIP-Law Clerk accepts. */
	lawClerkEarliest: string;
}

/** October 1 of the given year, as an ISO date. */
const octoberFirst = (year: number): string => `${year}-10-01`;

/**
 * Derive every threshold from the cycle year.
 *
 * `servedJdCutoff` overrides the too-senior floor with the value the server
 * resolved (`OasisPublicConfig.jdEligibilityCutoff`), which is the per-cycle override
 * when OARM set one and the standing October 1 rule otherwise. Pass it whenever it is
 * available.
 *
 * Deriving the floor locally is a FALLBACK for a failed config fetch, not the
 * intended path: the server applies its own resolved cutoff at submit, so a client
 * that derives while the server overrides will disagree with it — silently blocking an
 * eligible applicant, or letting one through to be rejected at the final step.
 */
export const programEligibilityThresholds = (cycleYear: number, servedJdCutoff?: string): ProgramEligibilityThresholds => ({
	tooSeniorBefore: servedJdCutoff ?? octoberFirst(cycleYear - 4),
	hpStudentLatest: octoberFirst(cycleYear),
	lawClerkEarliest: octoberFirst(cycleYear - 1),
});

/** Which rule fired. `null` means nothing is blocking. */
export type ProgramEligibilityBlockCode = 'first_semester' | 'too_senior' | 'hp_requires_pathways' | 'law_clerk_too_early';

/**
 * Applicant-facing message per rule. Wording supplied by OARM.
 *
 * Exported because the too-senior stop is deliberately enforced on two screens
 * (here and the Law Degree card) and both must say exactly the same thing — a
 * shared constant makes that true by construction rather than by review.
 */
export const PROGRAM_ELIGIBILITY_MESSAGES: Record<ProgramEligibilityBlockCode, string> = {
	first_semester:
		'First Year - First Semester Law Student applicants are not eligible for the Attorney General’s Honors Program (HP) or a Summer Law Internship Program (SLIP).',
	too_senior: "You're too senior and not eligible for any program.",
	hp_requires_pathways: "You're not eligible for the Attorney General's Honors Program and must choose SLIP-Pathways.",
	law_clerk_too_early: 'You are not eligible for the SLIP-Law Clerk program based on your graduation date.',
};

export interface ProgramEligibilityBlock {
	code: ProgramEligibilityBlockCode;
	/** Applicant-facing message. Wording supplied by OARM. */
	message: string;
	/** True when the applicant's route is to switch to SLIP-Pathways. */
	suggestsPathways: boolean;
}

export interface ProgramEligibilityInput {
	/** '' while the applicant has not chosen yet. */
	program: 'honors_program' | 'slip' | '';
	slipTrack: 'slip_pathways' | 'slip_law_clerk' | '';
	applicantStatus: 'current_law_student' | 'law_school_graduate' | '';
	/**
	 * How far into law school a current student is. '' until answered, and
	 * irrelevant for a graduate. Only `1L_first_semester` blocks.
	 */
	currentYear?: string;
	/**
	 * The graduation date to judge — expected or conferred. Each screen passes
	 * the date IT collects, so a block always reflects what the applicant can
	 * see and edit on that screen.
	 */
	graduationDate: string;
	cycleYear: number;
	/**
	 * The server-resolved J.D. recency cutoff, when config is available. Overrides
	 * the locally derived floor — see {@link programEligibilityThresholds}.
	 */
	jdEligibilityCutoff?: string;
}

/**
 * `LawStudentYear` value for a 1L in their first semester.
 *
 * Compared as a plain string so this module stays free of `shared-resources`
 * imports (`core` must not depend on the type package for a single literal). The
 * value is asserted against the real union in the spec.
 */
const FIRST_SEMESTER = '1L_first_semester';

/**
 * Evaluate the eligibility rules. Returns the first block that applies, most
 * severe first, or `null` when the applicant is clear or has not entered enough
 * yet to judge.
 *
 * Pure and derived from current values only — nothing is remembered. Correcting
 * a mistyped date or a mis-selected year clears the block on the next
 * evaluation, which is the required behaviour for every hard stop in this
 * application.
 */
export const evaluateProgramEligibility = (input: ProgramEligibilityInput): ProgramEligibilityBlock | null => {
	const { program, slipTrack, applicantStatus, currentYear, graduationDate, cycleYear, jdEligibilityCutoff } = input;

	// 1. First semester of 1L — too early for every program, whichever one they
	//    picked. Checked before the date rules because it does not depend on a
	//    date at all: a first-semester 1L is blocked even with nothing else filled
	//    in, and telling them about their graduation date instead would be noise.
	if (applicantStatus === 'current_law_student' && currentYear === FIRST_SEMESTER) {
		return {
			code: 'first_semester',
			message: PROGRAM_ELIGIBILITY_MESSAGES.first_semester,
			suggestsPathways: false,
		};
	}

	// Nothing further to judge until there is a complete date on screen.
	if (!isCompleteDate(graduationDate)) return null;

	const t = programEligibilityThresholds(cycleYear, jdEligibilityCutoff);

	// 2. Too senior — conferred before the floor. Checked next: it rules out
	//    every program, so a more specific message would understate the problem.
	if (graduationDate < t.tooSeniorBefore) {
		return {
			code: 'too_senior',
			message: PROGRAM_ELIGIBILITY_MESSAGES.too_senior,
			suggestsPathways: false,
		};
	}

	// 3. Honors Program has a ceiling for current students: graduating after it
	//    means interning before graduation, which is what SLIP-Pathways is for.
	if (program === 'honors_program' && applicantStatus === 'current_law_student' && graduationDate > t.hpStudentLatest) {
		return {
			code: 'hp_requires_pathways',
			message: PROGRAM_ELIGIBILITY_MESSAGES.hp_requires_pathways,
			suggestsPathways: true,
		};
	}

	// 4. SLIP-Law Clerk covers the summer between graduation and a clerkship, so
	//    a degree conferred before the floor is too early for that track.
	if (program === 'slip' && slipTrack === 'slip_law_clerk' && graduationDate < t.lawClerkEarliest) {
		return {
			code: 'law_clerk_too_early',
			message: PROGRAM_ELIGIBILITY_MESSAGES.law_clerk_too_early,
			suggestsPathways: false,
		};
	}

	return null;
};

/** Format an ISO date for display in supporting copy (e.g. "October 1, 2023"). */
export const formatThresholdDate = (isoDate: string): string =>
	new Date(`${isoDate}T00:00:00`).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
